/**
 * NoteAuditsList Component
 *
 * Displays list of note audits with filtering and pagination
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useNoteAuditsList } from '../../hooks/useNoteAuditsList';
import { isValidNoteId } from '../../utils/validation';
import { NoteAuditTableHeader, NoteAuditTableRow } from '../providers/detail/NoteAuditTableRow';

import { NotesFilters, type NotesFiltersState } from './NotesFilters';

interface NoteAuditsListProps {
  /** Filter by specific user ID */
  filterByUserId?: string;
  /** Organization ID for admin mode */
  organizationId?: string;
}

export const NoteAuditsList: React.FC<NoteAuditsListProps> = ({
  filterByUserId,
  organizationId,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { jotauditDashboard } = useEventTracking();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<NotesFiltersState>({});
  const [isFilterChanging, setIsFilterChanging] = useState(false);
  const previousFiltersRef = useRef<string>(JSON.stringify(filters));
  const previousAuditsLengthRef = useRef<number>(0);

  // Convert userIds array to comma-separated string
  const userIdsParam = useMemo(() => {
    if (filterByUserId) return filterByUserId;
    if (filters.userIds && filters.userIds.length > 0) {
      return filters.userIds.join(',');
    }
    return undefined;
  }, [filterByUserId, filters.userIds]);

  const {
    audits,
    total,
    page,
    perPage,
    hasMore,
    isLoading,
    isFetching,
    error,
    refetch,
    isEmpty,
  } = useNoteAuditsList({
    organizationId,
    userIds: userIdsParam,
    page: currentPage,
    perPage: 20,
    days: filters.days,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

  // Track filter changes for loading state
  useEffect(() => {
    const currentFiltersJson = JSON.stringify(filters);
    if (previousFiltersRef.current !== currentFiltersJson) {
      setIsFilterChanging(true);
      previousFiltersRef.current = currentFiltersJson;
    }
  }, [filters]);

  // Reset loading state when fetch completes
  useEffect(() => {
    if (isFilterChanging && !isFetching) {
      // Fetch has completed (whether from cache or network), reset the filter changing state
      setIsFilterChanging(false);
    }
  }, [isFilterChanging, isFetching]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Calculate pagination values
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const hasPrevious = currentPage > 1;

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious && !isLoading) {
      const newPage = Math.max(1, currentPage - 1);
      
      // Track pagination
      jotauditDashboard.notesPaginated({
        page_number: newPage,
        direction: 'previous',
        total_pages: totalPages,
        total_notes: total,
      });
      
      setCurrentPage(newPage);
    }
  }, [hasPrevious, isLoading, currentPage, totalPages, total, jotauditDashboard]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isLoading) {
      const newPage = currentPage + 1;
      
      // Track pagination
      jotauditDashboard.notesPaginated({
        page_number: newPage,
        direction: 'next',
        total_pages: totalPages,
        total_notes: total,
      });
      
      setCurrentPage(newPage);
    }
  }, [hasMore, isLoading, currentPage, totalPages, total, jotauditDashboard]);

  const handleViewAudit = useCallback(
    (noteId: string) => {
      if (!isValidNoteId(noteId)) return;

      jotauditDashboard.noteClicked({
        note_id: noteId,
        action: 'view_audit',
      });
      if (organizationId) {
        navigate(`/admin-v2/jotaudit/${organizationId}/note/${noteId}`);
      } else {
        navigate(`/jotaudit/note/${noteId}`);
      }
    },
    [navigate, jotauditDashboard, organizationId],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading audits</AlertTitle>
        {error}
      </Alert>
    );
  }

  const showLoadingState = isLoading || isFilterChanging;

  return (
    <Box>
      {/* Filters */}
      {!filterByUserId && (
        <NotesFilters
          filters={filters}
          onFiltersChange={setFilters}
          isLoading={showLoadingState}
        />
      )}

      {/* Header */}
      {!filterByUserId && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Note Audits
            </Typography>
            {!showLoadingState && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {total} {total === 1 ? 'audit' : 'audits'}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {/* Notes Table */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <NoteAuditTableHeader />

        {showLoadingState ? (
          <Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 48px',
                  alignItems: 'center',
                  px: 2,
                  py: 2,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                }}
              >
                <Box>
                  <Skeleton sx={{width: "70%", height: 20,  mb: 0.5 }} />
                  <Skeleton  sx={{ width: "50%", height: 16 }}/>
                </Box>
                <Skeleton  sx={{ width: "60%", height: 20 }}/>
                <Skeleton sx={{width: "40%", height: 24,  borderRadius: 1 }} />
                <Skeleton  sx={{ width: "50%", height: 20 }}/>
                <Skeleton variant="circular"  sx={{ width: 24, height: 24 }}/>
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No audits found</Typography>
          </Box>
        ) : (
          <Box>
            {audits.map(audit => (
              <NoteAuditTableRow
                key={audit.audit.note_audit_id}
                auditListItem={audit}
                onClick={() => handleViewAudit(audit.audit.note_id)}
              />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {total > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.grey[100]}`,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Page {currentPage} of {totalPages}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                disabled={!hasPrevious || isLoading}
                onClick={handlePreviousPage}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton 
                size="small" 
                disabled={!hasMore || isLoading}
                onClick={handleNextPage}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default NoteAuditsList;
